import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useAppSelector } from "@/store/hooks";
import { RootState } from "@/store/rootReducer";
import { LoadingStatus } from "@/enums/LoadingStatus.enum";
import { DefectSeverity } from "@/types/DefectCategory.type";
import { DefectDataByDivision, InspectionDefectData } from "@/store/types/InspectionDefectData.type";
import { API_ENDPOINTS } from "@/api/endpoints.constant";
import { clivApiRequest } from "@/utils/apiClient";
import { systemConfig } from "@/config/systemConfig";
import {
  clearDefectsByDivision,
  setDefectCodesAndSeverities,
  setEntityStatus,
} from "@/store/slices/masterDataSlice";
import { masterDataIdbService } from "@/store/idbStorage/masterDataIdbService";
import { Division } from "@/types/Division";
import * as Sentry from "@sentry/react";
import { store } from "@/store/store";
import { parseDefectCodesAndSeverity } from "@/utils/inspection.utils";
import { ALL_CONTROL_POINTS_ID } from "@/types/ControlPoint";

export const useDefectData = (controlPointId: string | null | undefined, divisions: Division[]) => {
  const dispatch = useDispatch();
  const defectsByDivision = useAppSelector(
    (state: RootState) => state.masterData.defectsByDivision
  );
  const severities = useAppSelector(
    (state: RootState) => state.masterData.severities
  );
  const status = useAppSelector(
    (state: RootState) => state.masterData.entityStatus["defects"] ?? LoadingStatus.IDLE
  );

  const loadGenerationRef = useRef(0);
  const loadingRef = useRef(false);

  useEffect(() => {
    if (!controlPointId) return;

    const currentState = store.getState() as RootState;
    const currentDefects = currentState.masterData.defectsByDivision;
    const currentStatus = currentState.masterData.entityStatus["defects"];
    const loadedControlPointId = currentState.masterData.loadedDefectControlPointId;
    if (
      loadedControlPointId === controlPointId &&
      currentDefects?.length > 0 &&
      currentStatus === LoadingStatus.SUCCEEDED
    ) {
      return;
    }

    if (
      loadedControlPointId === controlPointId &&
      currentStatus === LoadingStatus.LOADING
    ) {
      return;
    }
    if (loadingRef.current) return;

    if (loadedControlPointId !== controlPointId) {
      dispatch(clearDefectsByDivision());
    }

    const effectiveDivisions: Division[] =
      divisions?.length ? divisions : [{ id: "" } as Division];

    loadGenerationRef.current += 1;
    const generation = loadGenerationRef.current;

    const load = async () => {
      loadingRef.current = true;
      dispatch(setEntityStatus({ entity: "defects", status: LoadingStatus.LOADING }));

      try {
        const cachedSeverities = await masterDataIdbService.getSeverities();
        const divisionsWithIds = effectiveDivisions.map((d) => d.id || "");

        if (
          cachedSeverities &&
          cachedSeverities.controlPointId === controlPointId &&
          cachedSeverities.severities?.length
        ) {
          const cachedDefects: DefectDataByDivision[] = [];
          let allDivisionsFound = true;

          for (const divisionId of divisionsWithIds) {
            const defects = await masterDataIdbService.getDefectsByDivision(
              controlPointId,
              divisionId
            );
            if (!defects) {
              allDivisionsFound = false;
              break;
            }
            cachedDefects.push({ divisionId, defects });
          }

          if (allDivisionsFound && cachedDefects.length) {
            if (generation !== loadGenerationRef.current) return;
            dispatch(
              setDefectCodesAndSeverities({
                defectsByDivision: cachedDefects,
                severities: cachedSeverities.severities,
                controlPointId,
              })
            );
            dispatch(setEntityStatus({ entity: "defects", status: LoadingStatus.SUCCEEDED }));
            return;
          }
        }

        const defectDataMap: Record<string, any> = {};
        let fetchedSeverities: DefectSeverity[] = [];

        const promises = effectiveDivisions.map(async (division) => {
          const params: Record<string, string> = {};
          if (controlPointId !== ALL_CONTROL_POINTS_ID) {
            params.controlPointId = controlPointId;
          }
          if (division.id && division.id.trim() !== "") {
            params.divisionId = division.id;
          }

          const response = await clivApiRequest<{
            defects: Array<any>;
            severities: Array<DefectSeverity>;
          }>({
            url: `${systemConfig.apiUrl}/${API_ENDPOINTS.META.DEFECT_CATEGORIES}`,
            params,
          });

          if (response.ok && response.data) {
            const parsedData = parseDefectCodesAndSeverity(response);
            const divisionKey = division.id || "";
            defectDataMap[divisionKey] = parsedData;
            if (fetchedSeverities.length === 0 && parsedData.severities?.length) {
              fetchedSeverities = parsedData.severities.sort((a, b) => a.order - b.order);
            }
          } else {
            Sentry.captureException(new Error("Error fetching defect categories"), {
              extra: { params, data: response },
            });
          }
        });

        await Promise.all(promises);

        if (generation !== loadGenerationRef.current) return;

        const defectsByDivisionResult: DefectDataByDivision[] = Object.keys(defectDataMap).map(
          (divisionId) => ({ divisionId, defects: defectDataMap[divisionId].defects })
        );

        const defectData: InspectionDefectData = {
          defectsByDivision: defectsByDivisionResult,
          severities: fetchedSeverities,
          controlPointId,
        };

        dispatch(setDefectCodesAndSeverities(defectData));

        await masterDataIdbService.setSeverities(controlPointId, fetchedSeverities);
        for (const division of defectsByDivisionResult) {
          await masterDataIdbService.setDefectsByDivision(
            controlPointId,
            division.divisionId,
            division.defects
          );
        }

        dispatch(setEntityStatus({ entity: "defects", status: LoadingStatus.SUCCEEDED }));
      } catch (error) {
        if (generation !== loadGenerationRef.current) return;
        console.error("Error fetching defect categories:", error);
        Sentry.captureException(error as Error);
        dispatch(setEntityStatus({ entity: "defects", status: LoadingStatus.FAILED }));
      } finally {
        loadingRef.current = false;
      }
    };

    void load();
  }, [controlPointId, divisions?.length, dispatch]);

  return {
    defectsByDivision,
    severities,
    status,
  };
};
